/**
 * Email WebSocket Sync Service
 * 
 * Provides real-time email synchronization across all devices using WebSocket channels.
 * When an email is moved, deleted, read, or starred on one device, all other
 * connected devices receive the update immediately (Exchange-like behavior).
 * 
 * @refactored February 7, 2026 - Phase B5 (Channel Migration)
 * Uses TotalAccessWebSocket singleton with channel-based subscriptions.
 */

import { wsClient, type ConnectionStatus, type ChannelCallback } from '@/lib/websocket';
import { authService } from '@/lib/authService';

type EmailSyncCallback = (data: EmailSyncEvent) => void;
type ConnectionCallback = (connected: boolean) => void;

export interface EmailSyncEvent {
  type: string;
  action?: string;
  messageId?: string;
  messageIds?: string[];
  read?: boolean;
  starred?: boolean;
  folder?: string;
  toFolder?: string;
  fromFolder?: string;
  email?: {
    messageId: string;
    threadId?: string;
    thread_id?: string;
    subject?: string;
    from?: { name?: string; email: string; address?: string } | string;
    to?: Array<{ name?: string; email?: string; address?: string }>;
    date?: string;
    folder?: string;
    is_read?: boolean;
    read?: boolean;
    inReplyTo?: string;
    snippet?: string;
    accountId?: number;
    last_message_folder?: string;
    isReply?: boolean;
    // For email_moved action
    fromFolder?: string;
    toFolder?: string;
    // Update fields for email_updated action
    flags?: {
      seen?: boolean;
      flagged?: boolean;
      answered?: boolean;
      draft?: boolean;
    };
    replied_at?: string;
    reply_message_id?: string;
  };
  // For batch new_emails action
  emails?: Array<{
    messageId: string;
    threadId?: string;
    thread_id?: string;
    subject?: string;
    from?: { name?: string; email?: string; address?: string } | string;
    date?: string;
    folder?: string;
    snippet?: string;
    inReplyTo?: string;
    is_read?: boolean;
    read?: boolean;
    accountId?: number;
  }>;
  email_count?: number;
  timestamp?: string;
  source_session?: string;
  error?: string;
  syncing?: boolean;
  new_email_count?: number;
  last_sync?: string;
  // For thread_read_status_changed and thread_moved actions
  thread_id?: string;
  read_by_users?: number[];
  changed_by_user_id?: number;
  mailbox_id?: number;
  is_read?: boolean;
  // For thread_moved action
  moved_by_user_id?: number;
  // For email queue updates
  queueId?: string;
  status?: string;
  retryCount?: number;
  // For security_update action
  threadId?: string;
  updateAction?: string;
  securityStatus?: unknown;
  deepAnalysis?: unknown;
}

class EmailSyncService {
  private callbacks: Set<EmailSyncCallback> = new Set();
  private connectionCallbacks: Set<ConnectionCallback> = new Set();
  private unsubscribeChannel: (() => void) | null = null;
  private unsubscribeConnection: (() => void) | null = null;
  private isSubscribed = false;
  private currentUserId: number | null = null;

  /**
   * Connect and subscribe to email sync events for the current user
   */
  connect(): void {
    const tokens = authService.getTokens();
    const user = authService.getUser();
    
    if (!tokens?.access_token || !user?.id) {
      console.warn('[EmailSync] No auth token or user available');
      return;
    }

    // Already subscribed to this user's channel
    if (this.isSubscribed && this.currentUserId === user.id) {
      console.log('[EmailSync] Already subscribed');
      return;
    }

    // If switching users, unsubscribe from old channel first
    if (this.currentUserId && this.currentUserId !== user.id) {
      this.disconnect();
    }

    this.currentUserId = user.id;
    const channelName = `email:user:${user.id}`;

    console.log('[EmailSync] Subscribing to channel:', channelName);

    // Connect WebSocket if not connected
    if (!wsClient.isConnected()) {
      wsClient.connect().catch(err => {
        console.error('[EmailSync] WebSocket connect error:', err);
      });
    }

    // Listen for connection status changes
    this.unsubscribeConnection = wsClient.onConnection((status: ConnectionStatus) => {
      const connected = status === 'connected';
      this.notifyConnectionChange(connected);
      
      // Re-subscribe when reconnected
      if (connected && !this.isSubscribed && this.currentUserId) {
        this.subscribeToChannel();
      }
    });

    // Subscribe to email channel
    this.subscribeToChannel();
  }

  private subscribeToChannel(): void {
    if (!this.currentUserId) return;
    
    const channelName = `email:user:${this.currentUserId}`;
    
    // Channel callback handler
    const handleChannelMessage: ChannelCallback = (data, channel) => {
      const event = data as EmailSyncEvent;
      
      // Log the event
      console.log('[EmailSync] Received:', event.action || event.type || 'event');
      
      // Notify all subscribers
      this.callbacks.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('[EmailSync] Callback error:', error);
        }
      });
    };

    // Subscribe to channel
    this.unsubscribeChannel = wsClient.subscribe(channelName, handleChannelMessage);
    this.isSubscribed = true;
    
    console.log('[EmailSync] Subscribed to', channelName);
  }

  /**
   * Disconnect from WebSocket and unsubscribe
   */
  disconnect(): void {
    console.log('[EmailSync] Disconnecting...');

    if (this.unsubscribeChannel) {
      this.unsubscribeChannel();
      this.unsubscribeChannel = null;
    }

    if (this.unsubscribeConnection) {
      this.unsubscribeConnection();
      this.unsubscribeConnection = null;
    }

    this.isSubscribed = false;
    this.currentUserId = null;
    this.notifyConnectionChange(false);
  }

  /**
   * Subscribe to email sync callbacks
   */
  subscribe(callback: EmailSyncCallback): () => void {
    this.callbacks.add(callback);

    // Auto-connect if not connected
    if (!this.isSubscribed) {
      this.connect();
    }

    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Subscribe to connection status changes
   */
  onConnectionChange(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.add(callback);
    try {
      callback(this.isConnected());
    } catch (error) {
      console.error('[EmailSync] Connection callback error:', error);
    }
    return () => {
      this.connectionCallbacks.delete(callback);
    };
  }

  /**
   * Send an email action via WebSocket (for real-time sync)
   * Note: This publishes to the user's email channel
   */
  sendEmailAction(action: string, data: Record<string, unknown>): void {
    if (!wsClient.isConnected() || !this.currentUserId) {
      console.warn('[EmailSync] Cannot send action - not connected');
      return;
    }

    const channelName = `email:user:${this.currentUserId}`;
    wsClient.publish(channelName, {
      type: 'email_action',
      action,
      ...data
    }).catch(err => {
      console.error('[EmailSync] Failed to send action:', err);
    });
  }

  /**
   * Request email sync from IMAP
   */
  requestSync(folder: string = 'INBOX', force: boolean = false): void {
    if (!wsClient.isConnected() || !this.currentUserId) {
      console.warn('[EmailSync] Cannot request sync - not connected');
      return;
    }

    const channelName = `email:user:${this.currentUserId}`;
    wsClient.publish(channelName, {
      type: 'email_sync_request',
      folder,
      force
    }).catch(err => {
      console.error('[EmailSync] Failed to request sync:', err);
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return wsClient.isConnected() && this.isSubscribed;
  }

  /**
   * Get current session ID (to ignore own actions)
   * Note: Now uses wsClient's internal tracking
   */
  getSessionId(): string | null {
    // The wsClient handles session correlation internally
    return null;
  }

  // Private methods

  private notifyConnectionChange(connected: boolean): void {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('[EmailSync] Connection callback error:', error);
      }
    });
  }
}

// Singleton instance
export const emailSyncService = new EmailSyncService();

// Default export for convenience
export default emailSyncService;

